import React, { useEffect, useState } from "react";

import { Grid2 as Grid, Grid2Props as GridProps } from "@mui/material";

import {
  Autocomplete,
  Button,
  Group,
  Modal,
  NumberInput,
  Paper,
  Select,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { useForm } from "@mantine/form";
import { ImagePositions, VehicleProps } from "../../lib/Types";

import { DatePickerInput } from "@mantine/dates";
import { IconCoin, IconPhoto, IconUpload, IconX } from "@tabler/icons-react";
import dayjs from "dayjs";
import { CarsAndModels, colors, Colors, ValidIDLength, years } from "../../lib/Data";
import { Endpoints } from "../../lib/Endpoints";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  validateEmail,
  validatePhone,
} from "../../lib/Methods";
import { PerformRequest, UploadFiles } from "../../lib/usePerformRequest";

import "./styles.scss";
interface VehicleModalProps {
  action: "update" | "add";
  isFormOpen: boolean;
  OpenForm: () => void;
  CloseForm: () => void;
  OnComplete?: () => any;
  vehicle?: VehicleFormProps;
  requestID?: string;
  shouldShowImages?: boolean;
  useCreateRoute?: boolean;
}

type VehicleFormProps = Omit<VehicleProps, "id" | "sold"> & {
  id?: string;
};

const VehicleForm: React.FC<VehicleModalProps> = ({
  requestID,
  action,
  isFormOpen,
  OpenForm,
  OnComplete,
  shouldShowImages,
  useCreateRoute,
  CloseForm,
  vehicle,
}) => {
  const form = useForm({
    initialValues: {
      id: "",
      email: "",
      name: "",
      phone: "",
      model: "",
      amount: "",
      odometer: "",
      notes: "Vehicle received!",
      purchaseDate: dayjs().toDate(),
      year: "",
      color: "",
      manufacturer: "",
      vin: "",
      driveable: "yes",
    },
    validate: {
      id: (value) =>
        action === "add"
          ? null
          : value.length === ValidIDLength
          ? null
          : "Vehicle must have a valid ID",
      email: (value) => (validateEmail(value) ? null : "Invalid email"),
      phone: (value) => (validatePhone(value) ? null : "Invalid phone"),
      name: (value) => (value && value.length > 2 ? null : "Enter client name"),
      model: (value) => (value && value.length > 2 ? null : "Enter model"),
      year: (value) => (value && value.length > 2 ? null : "Enter year"),
      odometer: (value) => (value && value.length > 0 ? null : "Enter mileage"),
      amount: (value) =>
        value && !isNaN(parseInt(value)) && parseInt(value) > 0 ? null : "Enter amount",
      color: (value) => (value && value.length > 2 ? null : "Enter color"),
      purchaseDate: (value) =>
        value && value.toString().length > 1 ? null : "Select date",
      manufacturer: (value) => (value && value.length > 2 ? null : "Enter manufacturer"),
      notes: (value) => (value && value.length > 1 ? null : "Enter notes"),
      vin: (value) => (value && value.length > 7 ? null : "Enter a valid vin"),
      driveable: (value) => (value && value.length > 1 ? null : "Specify condition"),
    },
  });
  useEffect(() => {
    if (vehicle) {
      const {
        id,
        previousOwner,
        model,
        year,
        odometer,
        color,
        manufacturer,
        notes,
        amount,
        purchaseDate,
        vin,
        driveable,
      } = vehicle;
      const { name, email, phone } = previousOwner;
      form.setValues({
        id,
        email,
        name,
        phone,
        model,
        year,
        odometer,
        color,
        manufacturer,
        notes,
        amount: amount as unknown as any,
        purchaseDate: dayjs(purchaseDate).toDate() as unknown as any,
        vin,
        driveable: driveable ? "yes" : "no",
      });
    } else {
      form.reset();
    }
  }, [vehicle]);
  const imagePositions: ImagePositions[] = ["front", "back", "left", "right"];

  const emptyTempImages: { position: ImagePositions; image: File | undefined }[] = [
    { position: "front", image: undefined },
    { position: "back", image: undefined },
    { position: "left", image: undefined },
    { position: "right", image: undefined },
  ];

  const gridItemProps: GridProps = {
    size: {
      xl: 3,
      lg: 3,
      md: 6,
      sm: 6,
      xs: 6,
    },
  };
  const [isLoading, setLoading] = useState<boolean>(false);
  const [tempImages, setTempImages] =
    useState<{ position: ImagePositions; image: File | undefined }[]>(emptyTempImages);

  const AddVehicleToInventory = async () => {
    const { hasErrors } = form.validate();
    const imagesError = useCreateRoute
      ? tempImages.map((t) => t.image).includes(undefined)
      : false;

    if (imagesError) {
      DefaultErrorNotification("Please select an image for each side of the vehicle!");
    }
    if (!hasErrors && !imagesError) {
      setLoading(true);
      const {
        email,
        name,
        phone,
        model,
        year,
        color,
        odometer,
        amount,
        notes,
        purchaseDate,
        manufacturer,
        vin,
        driveable,
      } = form.values;
      const images = tempImages.map((i) => i.image);

      const rUpload = await UploadFiles(images as File[]);
      if (rUpload && rUpload.status === 201) {
        const r = await PerformRequest({
          method: "POST",
          data: {
            email,
            phone,
            name,
            model,
            year,
            manufacturer,
            color,
            odometer,
            amount,
            notes,
            purchaseDate: purchaseDate.valueOf(),
            vin,
            images: rUpload.data.map((i) => i.path),
            driveable: driveable === "yes" ? true : false,
          },
          route: useCreateRoute
            ? Endpoints.GetCreateVehicleInInventory()
            : Endpoints.GetAddVehicleToInventory(requestID as string),
          callback: () => {
            setLoading(false);
          },
        });
        setLoading(false);
        if (r.status === 403) {
          DefaultErrorNotification(r?.message);
        }
        if (r.status === 201) {
          DefaultSuccessNotification("Vehicle added to inventory!");
          form.reset();
          CloseForm();
          if (OnComplete) {
            OnComplete();
          }
        }
      } else {
        setLoading(false);
        DefaultErrorNotification("There was an error uploading images!");
      }
    }
  };
  const UpdateVehicleInInventory = async () => {
    const { hasErrors } = form.validate();
    if (!hasErrors) {
      setLoading(true);
      const {
        id: vehicleID,
        email,
        name,
        phone,
        model,
        year,
        color,
        manufacturer,
        odometer,
        amount,
        notes,
        purchaseDate,
        vin,
        driveable,
      } = form.values;
      const r = await PerformRequest({
        method: "PUT",
        data: {
          email,
          phone,
          name,
          model,
          year,
          manufacturer,
          odometer,
          color,
          amount,
          notes,
          purchaseDate: purchaseDate.valueOf(),
          vin,
          driveable: driveable === "yes" ? true : false,
        },
        route: Endpoints.GetUpdateVehicleDetails(vehicleID),
        callback: () => {
          setLoading(false);
        },
      });
      setLoading(false);
      if (r.status === 403) {
        DefaultErrorNotification(r?.message);
      }
      if (r.status === 200) {
        DefaultSuccessNotification("Vehicle details updated!");
        form.reset();
        if (OnComplete) {
          OnComplete();
        }
        CloseForm();
      }
    }
  };
  const inputProps = {
    spellCheck: false,
    mb: 10,
  };
  return (
    <Modal
      centered
      opened={isFormOpen}
      className="vehicle-form-modal"
      onClose={CloseForm}
      withCloseButton={false}
    >
      <div className="vehicle-form-container">
        <Text tt="capitalize" c={Colors.BlackPrimary} fw={600} fz={20}>
          {action} Vehicle
        </Text>
        <div className="flex-row form-row align-center width-100 justify-between">
          <TextInput
            label="Client Email"
            placeholder="Previous owner email"
            w="49%"
            {...form.getInputProps("email")}
            {...inputProps}
          />
          <TextInput
            label="Client Name"
            placeholder="Previous owner name"
            w="49%"
            {...form.getInputProps("name")}
            {...inputProps}
          />
        </div>
        <div className="flex-row form-row align-center width-100 justify-between">
          <TextInput
            label="VIN"
            placeholder="Vehicle VIN"
            w="49%"
            {...form.getInputProps("vin")}
            {...inputProps}
          />
          <TextInput
            label="Odometer"
            placeholder="Odometer reading"
            w="49%"
            {...form.getInputProps("odometer")}
            {...inputProps}
          />
        </div>
        <div className="flex-row form-row align-center width-100 justify-between">
          <Select
            {...inputProps}
            label="Is Vehicle Driveable"
            w="49%"
            allowDeselect={false}
            placeholder="Is the Vehicle Drivable"
            {...form.getInputProps("driveable")}
            data={[
              { label: "Yes", value: "yes" },
              { label: "No", value: "no" },
            ]}
          />
          <TextInput
            label="Client Phone"
            placeholder="Previous owner phone"
            w="49%"
            leftSection={<Text>+1 &nbsp;</Text>}
            type="tel"
            {...form.getInputProps("phone")}
            {...inputProps}
          />
        </div>
        <div className="flex-row form-row align-center width-100 justify-between">
          <Autocomplete
            clearable
            label="Manufacturer"
            {...inputProps}
            w="49%"
            placeholder="Vehicle manufacturer"
            {...form.getInputProps("manufacturer")}
            data={CarsAndModels.map((c) => {
              return { label: c.brand, value: c.brand };
            })}
          />

          <Autocomplete
            clearable
            label="Model"
            w="49%"
            {...inputProps}
            placeholder="Select Model"
            {...form.getInputProps("model")}
            data={CarsAndModels.find(
              (c) => c.brand === form.values.manufacturer
            )?.models.map((model) => {
              return { label: model, value: model };
            })}
          />
        </div>
        <div className="flex-row form-row align-center width-100 justify-between">
          <NumberInput
            label="Amount"
            placeholder="Enter amount"
            hideControls
            min={1}
            leftSection={<IconCoin size={19} />}
            w="49%"
            {...form.getInputProps("amount")}
            {...inputProps}
          />
          <DatePickerInput
            label="Purchase Date"
            placeholder="Enter date of purchase"
            w="49%"
            maxDate={dayjs().toDate()}
            {...form.getInputProps("purchaseDate")}
            {...inputProps}
          />
        </div>
        <div className="flex-row form-row align-center width-100 justify-between">
          <Autocomplete
            clearable
            label="Year"
            {...inputProps}
            {...form.getInputProps("year")}
            placeholder="Vehicle year e.g 2001"
            data={years.map((c) => {
              return { label: c, value: c };
            })}
            w="49%"
          />
          <Autocomplete
            clearable
            label="Color"
            w="49%"
            {...inputProps}
            placeholder="Color e.g Red"
            {...form.getInputProps("color")}
            data={colors.map((c) => {
              return { label: c, value: c };
            })}
          />
        </div>
        <Textarea
          label="Notes"
          placeholder="Enter notes"
          w="100%"
          {...form.getInputProps("notes")}
          {...inputProps}
        />
        {shouldShowImages && (
          <>
            <Text fw={600} fz={17} c={Colors.BlackPrimary}>
              Upload Vehicle Images
            </Text>
            <Grid container spacing={1.2}>
              {imagePositions.map((position) => {
                const hasImage = tempImages.find((i) => i.position === position);
                return (
                  <Grid {...gridItemProps} key={position}>
                    <div className="flex-row align-center">
                      <Text c="dimmed" tt="capitalize">
                        {position}
                      </Text>
                    </div>

                    <Dropzone
                      onDrop={(files) => {
                        const newImages = tempImages.map((i) => {
                          if (i.position === position) {
                            return { position, image: files[0] };
                          } else {
                            return i;
                          }
                        });
                        setTempImages(newImages);
                      }}
                      onReject={(files) => {
                        if (files && files[0]) {
                          DefaultErrorNotification(files[0]?.errors[0]?.message);
                        }
                      }}
                      maxFiles={1}
                      maxSize={5 * 1024 ** 2}
                      accept={IMAGE_MIME_TYPE}
                    >
                      {hasImage && hasImage.image ? (
                        <Paper
                          className="image"
                          style={{
                            backgroundImage: `url(${URL.createObjectURL(
                              hasImage.image
                            )})`,
                            cursor: isLoading ? "progress" : "pointer",
                          }}
                        ></Paper>
                      ) : (
                        <Group
                          justify="center"
                          gap="xl"
                          h={70}
                          style={{ pointerEvents: "none" }}
                        >
                          <Dropzone.Accept>
                            <IconUpload
                              size={52}
                              color="var(--mantine-color-blue-6)"
                              stroke={1.5}
                            />
                          </Dropzone.Accept>
                          <Dropzone.Reject>
                            <IconX
                              size={52}
                              color="var(--mantine-color-red-6)"
                              stroke={1.5}
                            />
                          </Dropzone.Reject>
                          <Dropzone.Idle>
                            <IconPhoto
                              size={52}
                              color="var(--mantine-color-dimmed)"
                              stroke={1.5}
                            />
                          </Dropzone.Idle>
                        </Group>
                      )}
                    </Dropzone>
                  </Grid>
                );
              })}
            </Grid>
            <br />
          </>
        )}
        <div className="flex-row align-center justify-between">
          <Button
            w="47%"
            loading={isLoading}
            onClick={action === "add" ? AddVehicleToInventory : UpdateVehicleInInventory}
            color={Colors.GreenPrimary}
            tt="capitalize"
          >
            {action}
          </Button>
          <Button w="47%" color={Colors.RedPrimary} onClick={CloseForm}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default VehicleForm;
